/**
 * Slips Away — Mobile Product Gallery
 * Replaces Palo Alto/Flickity product media with native CSS scroll snap on mobile.
 *
 * Scope:
 * - Activates only when the page initially loads below 768px.
 * - Leaves the desktop gallery unchanged.
 * - Supports thumbnails, variant featured media and AJAX product replacement.
 */
(() => {
  'use strict';

  const MOBILE_BREAKPOINT = 768;
  const SELECTORS = {
    gallery: '[data-product-single-media-group]',
    slider: '[data-product-single-media-slider]',
    slide: '[data-product-single-media-wrapper]',
    thumbs: '[data-product-single-media-thumbs]',
    thumb: '[data-thumbnail-id]',
    quickView: '[data-quick-view-item], .popup-quick-view',
  };

  const CLASSES = {
    gallery: 'slipaway-native-gallery',
    slider: 'slipaway-native-gallery__slider',
    slide: 'slipaway-native-gallery__slide',
    thumbs: 'slipaway-native-gallery__thumbs',
    activeThumb: 'is-nav-selected',
    selected: 'is-selected',
    moving: 'is-moving',
  };

  const controllers = new WeakMap();
  let scanTimer = 0;
  let observer = null;

  const isMobileViewport = () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;

  const directChild = (parent, className) => {
    if (!parent) return null;
    return Array.from(parent.children).find((child) => child.classList?.contains(className)) || null;
  };

  const clearFlickityStyles = (element) => {
    if (!element) return;

    element.classList.remove('flickity-enabled', 'is-draggable', 'is-pointer-down', CLASSES.moving);
    element.style.removeProperty('touch-action');
    element.style.removeProperty('height');

    element.querySelectorAll('.flickity-button, .flickity-page-dots').forEach((node) => node.remove());

    element.querySelectorAll(`${SELECTORS.slide}, ${SELECTORS.thumb}`).forEach((node) => {
      node.style.removeProperty('position');
      node.style.removeProperty('left');
      node.style.removeProperty('right');
      node.style.removeProperty('transform');
      node.style.removeProperty('top');
      node.style.removeProperty('height');
    });
  };

  /**
   * Flickity normally unwraps its viewport during destroy(). This fallback restores
   * the original children when the instance is already broken or unavailable.
   */
  const manuallyUnwrapFlickity = (element) => {
    const viewport = directChild(element, 'flickity-viewport');
    if (!viewport) return;

    const innerSlider = directChild(viewport, 'flickity-slider');
    if (innerSlider) {
      const fragment = document.createDocumentFragment();
      while (innerSlider.firstChild) fragment.appendChild(innerSlider.firstChild);
      element.insertBefore(fragment, viewport);
    }

    viewport.remove();
  };

  const destroyFlickity = (element) => {
    if (!element) return;

    const Flickity = window.theme?.Flickity || window.Flickity;
    let instance = null;

    try {
      if (Flickity && typeof Flickity.data === 'function') {
        instance = Flickity.data(element);
      }
    } catch (error) {
      console.warn('[Slips Away Gallery] Could not read Flickity instance.', error);
    }

    if (instance && typeof instance.destroy === 'function') {
      try {
        instance.destroy();
      } catch (error) {
        console.warn('[Slips Away Gallery] Flickity destroy failed; restoring DOM manually.', error);
      }
    }

    manuallyUnwrapFlickity(element);
    clearFlickityStyles(element);
  };

  class MobileProductGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.slider = null;
      this.thumbs = null;
      this.slides = [];
      this.thumbItems = [];
      this.activeIndex = 0;
      this.scrollFrame = 0;
      this.resizeFrame = 0;
      this.heightObserver = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onImageLoad = this.onImageLoad.bind(this);

      this.mount();
    }

    mount() {
      this.refreshElements();
      if (!this.slider || this.slides.length === 0) return;

      this.destroyThemeSliders();
      this.refreshElements();
      this.applyNativeMarkup();
      this.bindEvents();

      const initiallySelected = this.slides.findIndex((slide) => slide.classList.contains(CLASSES.selected));
      this.activeIndex = initiallySelected >= 0 ? initiallySelected : 0;
      this.selectIndex(this.activeIndex, {behavior: 'auto', force: true});
    }

    refreshElements() {
      this.slider = this.gallery.querySelector(SELECTORS.slider);
      this.thumbs = this.gallery.querySelector(SELECTORS.thumbs);
      this.slides = this.slider ? Array.from(this.slider.querySelectorAll(SELECTORS.slide)) : [];
      this.thumbItems = this.thumbs ? Array.from(this.thumbs.querySelectorAll(SELECTORS.thumb)) : [];
    }

    destroyThemeSliders() {
      destroyFlickity(this.slider);
      destroyFlickity(this.thumbs);
      document.ontouchmove = null;
    }

    applyNativeMarkup() {
      this.gallery.classList.add(CLASSES.gallery);
      this.gallery.setAttribute('data-slipaway-native-gallery', '');

      this.slider.classList.add(CLASSES.slider);
      this.slider.setAttribute('role', 'region');
      this.slider.setAttribute('aria-label', 'Product media');
      this.slider.setAttribute('tabindex', '0');
      this.slider.style.removeProperty('touch-action');

      this.slides.forEach((slide, index) => {
        slide.classList.add(CLASSES.slide);
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
        slide.style.removeProperty('position');
        slide.style.removeProperty('left');
        slide.style.removeProperty('transform');
      });

      if (this.thumbs) {
        this.thumbs.classList.add(CLASSES.thumbs);
        this.thumbs.setAttribute('role', 'tablist');
        this.thumbs.style.removeProperty('touch-action');

        this.thumbItems.forEach((thumb, index) => {
          thumb.setAttribute('role', 'tab');
          thumb.setAttribute('aria-label', `Show product media ${index + 1}`);
          thumb.style.removeProperty('position');
          thumb.style.removeProperty('left');
          thumb.style.removeProperty('transform');
        });
      }
    }

    bindEvents() {
      this.slider.removeEventListener('scroll', this.onScroll);
      this.slider.addEventListener('scroll', this.onScroll, {passive: true});

      window.removeEventListener('resize', this.onResize);
      window.addEventListener('resize', this.onResize, {passive: true});

      if (this.thumbs) {
        this.thumbs.removeEventListener('click', this.onThumbClick, true);
        this.thumbs.addEventListener('click', this.onThumbClick, true);
      }

      this.gallery.querySelectorAll('img').forEach((image) => {
        image.removeEventListener('load', this.onImageLoad);
        image.addEventListener('load', this.onImageLoad, {once: false});
      });
    }

    onThumbClick(event) {
      const thumb = event.target.closest(SELECTORS.thumb);
      if (!thumb || !this.thumbs?.contains(thumb)) return;

      const index = this.thumbItems.indexOf(thumb);
      if (index < 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.selectIndex(index, {behavior: 'smooth', force: true});
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const width = this.slider.clientWidth;
        if (!width) return;

        const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.slider.scrollLeft / width)));
        if (index !== this.activeIndex) this.updateActiveState(index);
      });
    }

    onResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.selectIndex(this.activeIndex, {behavior: 'auto', force: true});
      });
    }

    onImageLoad(event) {
      if (this.slides[this.activeIndex]?.contains(event.target)) {
        this.updateAdaptiveHeight();
      }
    }

    updateActiveState(index) {
      this.activeIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle(CLASSES.selected, active);
        slide.classList.toggle('media--hidden', !active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        slide.dispatchEvent(
          new CustomEvent(active ? 'theme:media:visible' : 'theme:media:hidden', {
            bubbles: true,
          })
        );
      });

      this.thumbItems.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle(CLASSES.activeThumb, active);
        thumb.classList.toggle(CLASSES.selected, active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');

        const link = thumb.querySelector('a, button');
        if (link) link.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.centerActiveThumbnail();
      this.updateAdaptiveHeight();
    }

    centerActiveThumbnail() {
      const activeThumb = this.thumbItems[this.activeIndex];
      if (!activeThumb || !this.thumbs) return;

      const targetLeft = activeThumb.offsetLeft - (this.thumbs.clientWidth - activeThumb.clientWidth) / 2;
      this.thumbs.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: 'smooth',
      });
    }

    updateAdaptiveHeight() {
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;

      requestAnimationFrame(() => {
        // Temporarily allow intrinsic measurement before applying the active height.
        const previousHeight = this.slider.style.height;
        this.slider.style.height = 'auto';
        const height = Math.ceil(activeSlide.getBoundingClientRect().height || activeSlide.scrollHeight || 0);
        this.slider.style.height = previousHeight;

        if (height > 0) {
          this.slider.style.height = `${height}px`;
        }
      });

      if (this.heightObserver) this.heightObserver.disconnect();
      if ('ResizeObserver' in window) {
        this.heightObserver = new ResizeObserver(() => {
          const height = Math.ceil(activeSlide.getBoundingClientRect().height || activeSlide.scrollHeight || 0);
          if (height > 0) this.slider.style.height = `${height}px`;
        });
        this.heightObserver.observe(activeSlide);
      }
    }

    selectIndex(index, options = {}) {
      const safeIndex = Math.max(0, Math.min(this.slides.length - 1, Number(index) || 0));
      const behavior = options.behavior || 'smooth';
      const left = safeIndex * this.slider.clientWidth;

      if (options.force || safeIndex !== this.activeIndex) {
        this.updateActiveState(safeIndex);
      }

      this.slider.scrollTo({left, top: 0, behavior});
    }

    selectMediaId(mediaId, behavior = 'smooth') {
      if (mediaId === undefined || mediaId === null) return;
      const id = String(mediaId);
      const index = this.slides.findIndex((slide) => String(slide.dataset.id || '') === id);
      if (index >= 0) this.selectIndex(index, {behavior, force: true});
    }

    /**
     * Re-run after Palo Alto or an AJAX replacement attempts to initialise Flickity.
     */
    repair() {
      const currentSlider = this.gallery.querySelector(SELECTORS.slider);
      if (!currentSlider) return;

      const themeReinitialised =
        currentSlider.classList.contains('flickity-enabled') ||
        Boolean(directChild(currentSlider, 'flickity-viewport'));

      if (themeReinitialised) {
        this.destroyThemeSliders();
        this.refreshElements();
        this.applyNativeMarkup();
        this.bindEvents();
        this.selectIndex(this.activeIndex, {behavior: 'auto', force: true});
      }
    }
  }

  const initialiseGallery = (gallery) => {
    if (!gallery || gallery.closest(SELECTORS.quickView)) return;

    const slider = gallery.querySelector(SELECTORS.slider);
    if (!slider) return;

    const existing = controllers.get(gallery);
    if (existing) {
      existing.repair();
      return;
    }

    const controller = new MobileProductGallery(gallery);
    if (controller.slider && controller.slides.length) controllers.set(gallery, controller);
  };

  const scan = () => {
    if (!isMobileViewport()) return;
    document.querySelectorAll(SELECTORS.gallery).forEach(initialiseGallery);
  };

  const scheduleScan = (delay = 60) => {
    clearTimeout(scanTimer);
    scanTimer = window.setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(scan));
    }, delay);
  };

  const onVariantChange = (event) => {
    const variant = event.detail?.variant;
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;

    const eventContainer = event.target instanceof Element ? event.target : document;
    const gallery =
      eventContainer.querySelector?.(`[data-slipaway-native-gallery]`) ||
      eventContainer.closest?.('[data-section-id]')?.querySelector?.(`[data-slipaway-native-gallery]`);

    if (!gallery) return;
    const controller = controllers.get(gallery);
    controller?.selectMediaId(mediaId, 'smooth');
  };

  const startObserver = () => {
    if (observer || !document.body) return;

    observer = new MutationObserver((mutations) => {
      const shouldScan = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return (
            node.matches?.(SELECTORS.gallery) ||
            node.querySelector?.(SELECTORS.gallery) ||
            node.classList?.contains('flickity-viewport')
          );
        });
      });

      if (shouldScan) scheduleScan(80);
    });

    observer.observe(document.body, {childList: true, subtree: true});
  };

  document.addEventListener('theme:variant:change', onVariantChange);
  document.addEventListener('shopify:section:load', () => scheduleScan(100));
  document.addEventListener('DOMContentLoaded', () => {
    startObserver();
    scheduleScan(120);
  });

  window.addEventListener('load', () => scheduleScan(40));

  if (document.readyState !== 'loading') {
    startObserver();
    scheduleScan(120);
  }
})();
